/* eslint-disable @typescript-eslint/naming-convention */
// here we can implement analytics events triggering functions

import type { TBookId } from '@shared/jp/product';
import type { IAddressBillingData, IOrderData } from '@shared/jp/interfaces';
import { Logger } from '@shared/common/logger';
import type { TStepId } from './app/pages/order-page/interfaces';
import { environment } from './environments/environment';

const triggerCheck = <FN, ARGS extends any[]>(
  log: Logger,
  fn: FN,
  ...args: ARGS
) => {
  if (typeof fn === 'function') {
    fn(...args);
  } else {
    // const level = environment.production ? 'error' : 'info';

    // log[level](`trigger doesn't exist, arguments:`, ...args);
    throw new Error(
      `trigger doesn't exist, arguments:\n${JSON.stringify(
        args,
        undefined,
        2,
      )}`,
    );
  }
};

const gtagTriggerLog = new Logger('gtagTrigger');
const gtagTrigger: Gtag.Gtag = (...args: any[]) =>
  triggerCheck(gtagTriggerLog, window.gtag, ...args);

// const fbqTriggerLog = new Logger('fbqTrigger');
// export const fbqTrigger: facebook.Pixel.Event = (...args: any[]) =>
//   triggerCheck(fbqTriggerLog, window.fbq, ...args);

// const _ltTriggerLog = new Logger('_ltTrigger');
// export const _ltTrigger = (...args: any) =>
//   triggerCheck(_ltTriggerLog, window._lt, ...args);

const ytagTriggerLog = new Logger('ytagTrigger');
const ytagTrigger = (...args: any) =>
  triggerCheck(ytagTriggerLog, window.ytag, ...args);

// const pintrkTriggerLog = new Logger('pintrkTrigger');
// export const pintrkTrigger = (...args: any) =>
//   triggerCheck(pintrkTriggerLog, window.pintrk, ...args);

// const uetqTriggerLog = new Logger('uerqTrigger');
// export const uetqTrigger = (...args: any) =>
//   triggerCheck(uetqTriggerLog, window._uetq, ...args);

// * Google Anlytics section

/**
 * set of google analytics events we are waiting for
 */
export enum GAEvents {
  thankyou = 'thankyou',
  page_view = 'page_view',
}

export const GAGetEvents = {
  getBookPreview: (bookId: TBookId) => `book_preview_${bookId}`,
  getAddToCart: (bookId: TBookId) => `add_to_cart_${bookId}`,
  getCheckoutStep: (stepId: TStepId) => `checkout_step_${stepId}`,
};

/**
 * triggers Google Analytics event by
 * ! ga('track', `event`)
 * @param event - one of `FBEvents` values
 */
export const GATrack = (
  event: GAEvents | string,
  eventParams: Gtag.ControlParams | Gtag.EventParams | Gtag.CustomParams = {},
) => {
  if ('event_callback' in eventParams) {
    throw new Error('event_callback is not allowed here');
  }

  tryCatch(`GATrack(${event})`, (event_callback) => {
    gtagTrigger('event', event, {
      event_category: 'Default',
      send_to: 'G-Y5XNMWLYT4',
      event_callback,
      ...eventParams,
    });
  }).catch((e) => console.warn(e));
};

// * Facebook Pixel section

/**
 * set of facebook pixel events we are waiting for
 */
// export enum FBEvents {
//   viewContent = 'ViewContent',
//   addToCart = 'AddToCart',
//   initiateCheckout = 'InitiateCheckout',
//   purchase = 'Purchase',
// }

/**
 * triggers Facebook Pixel event by
 * fbq('track', `event`)
 * @param event - one of `FBEvents` values
 */
// export const FBTrack = (
//   event: FBEvents,
//   parameters?:
//     | facebook.Pixel.ViewContentParameters
//     | facebook.Pixel.SearchParameters
//     | facebook.Pixel.AddToCartParameters
//     | facebook.Pixel.AddToWishlistParameters
//     | facebook.Pixel.InitiateCheckoutParameters
//     | facebook.Pixel.AddPaymentInfoParameters
//     | facebook.Pixel.PurchaseParameters
//     | facebook.Pixel.LeadParameters
//     | facebook.Pixel.CompleteRegistrationParameters,
// ) =>
//   parameters
//     ? fbqTrigger('track', event, parameters)
//     : fbqTrigger('track', event);

const tryCatchLog = new Logger('tryCatch');
const tryCatch = async (
  label: string,
  fn: (cb: () => void) => void,
  timeout = 3000,
) => {
  const log = tryCatchLog;

  try {
    await new Promise<void>((resolve, reject) => {
      let resolved = false;

      /** resolves anyway in `timeout` milliseconds */
      const t = setTimeout(() => {
        if (environment.production) {
          log.error(`${label} terminated by timeout`);
        }

        resolved = true;
        resolve();
      }, timeout);

      try {
        fn(() => {
          if (resolved) return;

          clearTimeout(t);
          resolved = true;
          resolve();
        });
      } catch (e) {
        reject(e);
      }
    });
  } catch (e) {
    if (environment.production) {
      log.error(label, e);
    }
  }
};

export const sendConversionEvent = async (orderData: {
  addressBillingData: IAddressBillingData;
  orderId: string;
}) => {
  // FBTrack(FBEvents.purchase, {
  //   value: 3900,
  //   currency: 'JPY',
  // });

  // * new approach
  await tryCatch("gtagTrigger('set', 'user_data'", (cb) => {
    gtagTrigger('set', 'user_data', {
      //  ***Change yourEmailVariable to the actual Javascript variable name where you are storing the user’s email data. Do the same for the other variables below. Make sure the values aren't hashed.
      email: orderData.addressBillingData.email,
      phone_number: `+81${orderData.addressBillingData.phone.replace(
        /(^0|\D)/g,
        '',
      )}`,
      // address: {
      //   first_name: orderData.addressBillingData.firstName,
      //   last_name: orderData.addressBillingData.lastName,
      //   // street: yourStreetAddressVariable,
      //   // city: yourCityVariable,
      //   region: orderData.addressBillingData.state,
      //   postal_code: orderData.addressBillingData.postalCode.replace(/\D/g, ''),
      //   country: 'JP',
      // },
    });

    cb();
  });
  await tryCatch(
    "gtagTrigger('event', GAEvents.thankyou",
    (event_callback: () => void) => {
      gtagTrigger('event', GAEvents.thankyou, {
        event_category: 'Default',
        send_to: 'G-Y5XNMWLYT4',
        transaction_id: orderData.orderId,
        event_callback,
      });
    },
  );
  await tryCatch(
    "gtagTrigger('event', 'conversion'",
    (event_callback: () => void) => {
      gtagTrigger('event', 'conversion', {
        send_to: 'AW-16451283989/uRlUCNCflpAZEJXYyqQ9',
        transaction_id: orderData.orderId,
        event_callback,
      });
    },
  );
  // _ltTrigger(
  //   'send',
  //   'cv',
  //   {
  //     type: 'Conversion',
  //   },
  //   ['1011300d-ebc2-41d8-bbab-99cb084c7a71'],
  // );
  await tryCatch('ytagTrigger(yjad_conversion)', (cb) => {
    ytagTrigger({
      type: 'yjad_conversion',
      config: {
        yahoo_ydn_conv_io: '2cmG-KM8xbXZgEBG6--n8Q..',
        yahoo_ydn_conv_label: 'F0EGYUHKNOJTV0TLJM1160565',
        yahoo_ydn_conv_transaction_id: '',
        yahoo_ydn_conv_value: '4500',
        yahoo_email: '',
        yahoo_phone_number: '',
      },
    });

    cb();
  });
  await tryCatch('ytagTrigger(yss_conversion)', (cb) => {
    ytagTrigger({
      type: 'yss_conversion',
      config: {
        yahoo_conversion_id: '1001342594',
        yahoo_conversion_label: 'FFMhCL2NiswZEO-QqZA-',
        yahoo_conversion_value: '4500',
      },
    });

    cb();
  });

  // const tempErr = console.error;
  // console.error = console.warn;
  // pintrkTrigger('load', '2612978562674', {
  //   em: orderData.addressBillingData.email,
  // });
  // pintrkTrigger('page');
  // console.error = tempErr;

  // pintrkTrigger('track', 'checkout', {
  //   value: 4200,
  //   currency: 'JPY',
  //   order_id: orderData.orderId,
  // });
  // uetqTrigger('event', 'purchase', {});

  // set 1000 ms timeout to make sure analytics is tracked
  await tryCatch('timeout', (cb) => {
    setTimeout(() => cb(), 1000);
  });
};

export const sendConversionEventByOrder = async (orderData: IOrderData) => {
  const { addressBillingData, orderId } = orderData;

  if (!addressBillingData) {
    console.error('!addressBillingData');
    return;
  }

  await sendConversionEvent({
    addressBillingData,
    orderId,
  });
};
